/**
 * Compositor: texto, nota interna, anexos (botão ou colar imagem), voz,
 * respostas rápidas ("/atalho") e resposta citada. Envio otimista: a
 * mensagem aparece na hora e o status real chega por tempo real.
 */
import type { ConversaResumo, EstadoCopiloto, MensagemDto, RespostaRapida } from "@ydchat/shared";
import { aplicarVariaveis } from "@ydchat/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Clock,
  FileText,
  LayoutTemplate,
  Loader2,
  Mic,
  Paperclip,
  Send,
  Settings2,
  StickyNote,
  X,
  Zap,
} from "lucide-react";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
} from "react";
import { toast } from "sonner";
import { GerenciarRespostas } from "@/components/configuracoes/respostas-rapidas";
import { Botao } from "@/components/ui/botao";
import { PainelLateral } from "@/components/ui/painel-lateral";
import { api, ErroApi } from "@/lib/api";
import { enviarArquivo, formatarTamanho, useRespostasRapidas } from "@/lib/central";
import { chaveCopiloto } from "@/lib/copiloto";
import { cn } from "@/lib/cn";
import { useUsuario } from "@/lib/sessao";
import { rotuloTipo } from "./balao";
import {
  BarraCopiloto,
  MenuCopiloto,
  useCopilotoConversa,
  type UsoSugestaoLocal,
} from "./copiloto";
import { BarraGravacao, gravacaoSuportada, useGravador } from "./gravador";
import { PainelModelos } from "./modelos";
import { FaixaFluxo } from "./fluxo";
import { FaixaPonte } from "./ponte";
import { useAtalho } from "@/lib/atalhos";

export function Compositor({
  conversa,
  respondendoA,
  onLimparResposta,
  anexoExterno,
  onAnexoConsumido,
}: {
  conversa: ConversaResumo;
  respondendoA: MensagemDto | null;
  onLimparResposta: () => void;
  /** Arquivo solto na conversa (arrastar e soltar). */
  anexoExterno: File | null;
  onAnexoConsumido: () => void;
}) {
  const qc = useQueryClient();
  const usuario = useUsuario();
  const [texto, setTexto] = useState("");
  const [interna, setInterna] = useState(false);
  const [anexo, setAnexo] = useState<File | null>(null);
  const [modelosAbertos, setModelosAbertos] = useState(false);
  const [gerenciandoRespostas, setGerenciandoRespostas] = useState(false);
  const [indiceSugestao, setIndiceSugestao] = useState(0);
  // Sugestão da IA em uso (para registrar se foi enviada como veio ou editada).
  const [usoIa, setUsoIa] = useState<UsoSugestaoLocal | null>(null);
  const copiloto = useCopilotoConversa(conversa);
  useAtalho("sugerir", () => {
    if (copiloto.disponivel && conversa.modo === "humano") copiloto.pedir("sugerir");
  });
  const area = useRef<HTMLTextAreaElement>(null);
  const seletorArquivo = useRef<HTMLInputElement>(null);
  const chave = ["conversa", conversa.id, "mensagens"];
  const foraDaJanela =
    !conversa.janelaExpiraEm || new Date(conversa.janelaExpiraEm).getTime() < Date.now();
  const bloqueado = foraDaJanela && !interna;

  useEffect(() => {
    setTexto("");
    setInterna(false);
    setAnexo(null);
    setUsoIa(null);
    area.current?.focus();
  }, [conversa.id]);

  useEffect(() => {
    if (anexoExterno) {
      setAnexo(anexoExterno);
      onAnexoConsumido();
    }
  }, [anexoExterno, onAnexoConsumido]);

  useEffect(() => {
    if (respondendoA) area.current?.focus();
  }, [respondendoA]);

  useLayoutEffect(() => {
    const el = area.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [texto]);

  // --- Respostas rápidas: "/" no início abre as sugestões.
  const respostas = useRespostasRapidas();
  const termo =
    texto.startsWith("/") && !texto.includes("\n") ? texto.slice(1).toLowerCase() : null;
  const sugestoes = useMemo(() => {
    if (termo === null) return [];
    const todas = respostas.data ?? [];
    // Atalho que começa com o termo vem primeiro; título só a partir de 2 letras.
    const porAtalho = todas.filter((r) => r.atalho.startsWith(termo));
    const porTitulo =
      termo.length >= 2
        ? todas.filter((r) => !porAtalho.includes(r) && r.titulo.toLowerCase().includes(termo))
        : [];
    return [...porAtalho, ...porTitulo].slice(0, 6);
  }, [termo, respostas.data]);
  useEffect(() => setIndiceSugestao(0), [termo]);

  const usarResposta = (r: RespostaRapida) => {
    setTexto(
      aplicarVariaveis(r.texto, { nomeCliente: conversa.contato.nome, vendedora: usuario.nome }),
    );
    void api.post(`/respostas-rapidas/${r.id}/uso`).catch(() => undefined);
    area.current?.focus();
  };

  // --- Envio otimista
  const adicionarOtimista = (m: Partial<MensagemDto> & { id: string }) => {
    const base: MensagemDto = {
      id: m.id,
      conversaId: conversa.id,
      direcao: "saida",
      autor: "usuario",
      autorUsuario: { id: usuario.id, nome: usuario.nome },
      tipo: "texto",
      texto: null,
      conteudo: {},
      midia: null,
      respostaAId: respondendoA?.id ?? null,
      respostaA: respondendoA
        ? {
            texto: respondendoA.texto ?? "",
            direcao: respondendoA.direcao,
            tipo: respondendoA.tipo,
          }
        : null,
      reacoes: [],
      status: "pendente",
      erro: null,
      criadoEm: new Date().toISOString(),
    };
    qc.setQueryData<MensagemDto[]>(chave, (atual) => [...(atual ?? []), { ...base, ...m }]);
  };
  const removerOtimista = (id: string) =>
    qc.setQueryData<MensagemDto[]>(chave, (atual) => atual?.filter((m) => m.id !== id));
  const atualizar = () => {
    void qc.invalidateQueries({ queryKey: chave });
    void qc.invalidateQueries({ queryKey: ["conversas"] });
    void qc.invalidateQueries({ queryKey: ["conversa", conversa.id, "dados"] });
  };

  const enviarTexto = useMutation({
    mutationFn: (v: {
      idCliente: string;
      texto: string;
      interna: boolean;
      respostaAId: string | null;
      sugestao?: { interacaoId: string; indice: number; editada: boolean } | null;
    }) => api.post(`/conversas/${conversa.id}/mensagens`, v),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: chave });
      adicionarOtimista({
        id: v.idCliente,
        direcao: v.interna ? "interna" : "saida",
        tipo: v.interna ? "nota_interna" : "texto",
        texto: v.texto,
        status: v.interna ? "enviada" : "pendente",
      });
    },
    onError: (e, v) => {
      removerOtimista(v.idCliente);
      setTexto(v.texto);
      toast.error(e instanceof ErroApi ? e.message : "Não foi possível enviar.");
    },
    onSettled: atualizar,
  });

  const enviarAnexo = useMutation({
    mutationFn: (v: {
      idCliente: string;
      arquivo: Blob;
      nome: string;
      legenda?: string;
      voz?: boolean;
      respostaAId: string | null;
    }) => enviarArquivo(conversa.id, v.arquivo, v),
    onMutate: (v) =>
      adicionarOtimista({
        id: v.idCliente,
        tipo: "documento",
        texto: v.voz ? "Enviando áudio…" : `Enviando ${v.nome}…`,
      }),
    onError: (e, v) => {
      removerOtimista(v.idCliente);
      toast.error(e instanceof ErroApi ? e.message : "Não foi possível enviar o arquivo.");
    },
    onSettled: atualizar,
  });

  const gravador = useGravador((audio, nome) =>
    enviarAnexo.mutate({
      idCliente: crypto.randomUUID(),
      arquivo: audio,
      nome,
      voz: true,
      respostaAId: respondendoA?.id ?? null,
    }),
  );

  const submeter = () => {
    if (bloqueado) return;
    const t = texto.trim();
    const resposta = respondendoA?.id ?? null;
    if (anexo) {
      enviarAnexo.mutate({
        idCliente: crypto.randomUUID(),
        arquivo: anexo,
        nome: anexo.name,
        legenda: t || undefined,
        respostaAId: resposta,
      });
      setAnexo(null);
    } else if (t) {
      enviarTexto.mutate({
        idCliente: crypto.randomUUID(),
        texto: t,
        interna,
        respostaAId: resposta,
        sugestao:
          usoIa && !interna
            ? {
                interacaoId: usoIa.interacaoId,
                indice: usoIa.indice,
                editada: t !== usoIa.original.trim(),
              }
            : null,
      });
      if (usoIa && !interna) limparSugestoes();
    } else return;
    setTexto("");
    setUsoIa(null);
    onLimparResposta();
  };

  // --- Copiloto: usar sugestão (editar) ou enviar como está.
  const limparSugestoes = () =>
    qc.setQueryData<EstadoCopiloto>(chaveCopiloto(conversa.id), (e) =>
      e ? { ...e, atual: null } : e,
    );
  const editarSugestao = (t: string, uso: UsoSugestaoLocal) => {
    setInterna(false);
    setTexto(t);
    setUsoIa(uso);
    requestAnimationFrame(() => {
      area.current?.focus();
      area.current?.setSelectionRange(t.length, t.length);
    });
  };
  const enviarSugestao = (t: string, uso: UsoSugestaoLocal) => {
    if (foraDaJanela) return;
    enviarTexto.mutate({
      idCliente: crypto.randomUUID(),
      texto: t,
      interna: false,
      respostaAId: respondendoA?.id ?? null,
      sugestao: { interacaoId: uso.interacaoId, indice: uso.indice, editada: false },
    });
    limparSugestoes();
    // O campo tinha a base da reescrita ou outra sugestão: não serve mais.
    if (uso.reescrita || usoIa) setTexto("");
    setUsoIa(null);
    onLimparResposta();
  };

  const tecla = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (sugestoes.length) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault();
        setIndiceSugestao(
          (i) => (i + (e.key === "ArrowDown" ? 1 : -1) + sugestoes.length) % sugestoes.length,
        );
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        usarResposta(sugestoes[indiceSugestao]!);
        return;
      }
      if (e.key === "Escape") {
        setTexto("");
        return;
      }
    }
    if (e.key === "Escape" && respondendoA) onLimparResposta();
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submeter();
    }
  };

  const colar = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const arquivo = [...e.clipboardData.files][0];
    if (arquivo && !interna) {
      e.preventDefault();
      setAnexo(arquivo);
    }
  };

  const podeGravar = gravacaoSuportada() && !interna && !anexo && !texto.trim();

  return (
    <div className="area-segura-inferior relative border-t border-neutral-200 bg-white">
      {bloqueado && (
        <div className="flex flex-wrap items-center gap-2 bg-warning-100 px-4 py-2 text-[13px] text-warning-500">
          <Clock size={14} strokeWidth={2} />
          <span className="flex-1">
            Passaram 24h desde a última mensagem da cliente. Para retomar, envie um modelo aprovado.
          </span>
          <Botao tamanho="sm" onClick={() => setModelosAbertos(true)}>
            <LayoutTemplate size={15} strokeWidth={1.75} /> Escolher modelo
          </Botao>
        </div>
      )}

      {respondendoA && (
        <div className="mx-auto flex max-w-3xl items-start gap-2 px-3 pt-2">
          <div className="min-w-0 flex-1 rounded-md border-l-[3px] border-gold-400 bg-neutral-50 px-2.5 py-1.5 text-[13px]">
            <span className="font-semibold text-gold-700">
              Respondendo {respondendoA.direcao === "entrada" ? "à cliente" : "à equipe"}
            </span>
            <p className="truncate text-neutral-600">
              {respondendoA.texto || rotuloTipo(respondendoA.tipo)}
            </p>
          </div>
          <button
            onClick={onLimparResposta}
            aria-label="Cancelar resposta"
            className="flex size-8 items-center justify-center rounded-md text-neutral-500 hover:bg-neutral-100"
          >
            <X size={16} strokeWidth={1.75} />
          </button>
        </div>
      )}

      {anexo && <PreviaAnexo arquivo={anexo} onRemover={() => setAnexo(null)} />}

      <FaixaPonte conversa={conversa} />
      <FaixaFluxo conversa={conversa} />
      {!interna && (
        <BarraCopiloto
          c={copiloto}
          ocultarSugestoes={bloqueado || !!anexo || termo !== null || conversa.modo === "ponte"}
          onEditar={editarSugestao}
          onEnviar={enviarSugestao}
        />
      )}

      {!!sugestoes.length && (
        <ul
          role="listbox"
          aria-label="Respostas rápidas"
          className="absolute bottom-full left-2 right-2 z-20 mb-2 max-w-xl overflow-hidden rounded-xl border border-neutral-200 bg-white p-1 shadow-lg md:left-14"
        >
          {sugestoes.map((r, i) => (
            <li key={r.id} role="option" aria-selected={i === indiceSugestao}>
              <button
                onMouseDown={(e) => {
                  e.preventDefault();
                  usarResposta(r);
                }}
                className={cn(
                  "w-full rounded-md px-3 py-2 text-left",
                  i === indiceSugestao ? "bg-gold-50" : "hover:bg-neutral-50",
                )}
              >
                <span className="text-sm font-semibold text-neutral-900">/{r.atalho}</span>
                <span className="ml-2 text-sm text-neutral-600">{r.titulo}</span>
                <span className="block truncate text-xs text-neutral-500">{r.texto}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mx-auto flex max-w-3xl items-end gap-1.5 p-2 md:gap-2 md:p-3">
        {gravador.gravando ? (
          <BarraGravacao
            segundos={gravador.segundos}
            onCancelar={() => gravador.parar(false)}
            onEnviar={() => gravador.parar(true)}
          />
        ) : (
          <>
            <BotaoIcone
              ativo={interna}
              tom="alerta"
              titulo="Nota interna (a cliente não vê)"
              onClick={() => setInterna((v) => !v)}
            >
              <StickyNote size={20} strokeWidth={1.75} />
            </BotaoIcone>
            {!interna && (
              <BotaoIcone
                titulo="Anexar arquivo"
                onClick={() => seletorArquivo.current?.click()}
                disabled={bloqueado}
              >
                <Paperclip size={20} strokeWidth={1.75} />
              </BotaoIcone>
            )}
            <input
              ref={seletorArquivo}
              type="file"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) setAnexo(f);
                e.target.value = "";
              }}
            />
            {!interna && <MenuCopiloto c={copiloto} texto={texto} soResumo={bloqueado} />}
            <BotaoIcone
              titulo="Respostas rápidas (digite /)"
              className="max-sm:hidden"
              onClick={() => {
                setTexto("/");
                area.current?.focus();
              }}
              disabled={bloqueado}
            >
              <Zap size={20} strokeWidth={1.75} />
            </BotaoIcone>
            <textarea
              ref={area}
              rows={1}
              value={texto}
              onChange={(e) => {
                setTexto(e.target.value);
                if (!e.target.value) setUsoIa(null);
              }}
              onKeyDown={tecla}
              onPaste={colar}
              disabled={bloqueado}
              placeholder={
                interna
                  ? "Nota interna para a equipe"
                  : anexo
                    ? "Legenda (opcional)"
                    : bloqueado
                      ? "Envie um modelo para retomar"
                      : "Escreva ou digite / para respostas rápidas"
              }
              aria-label="Mensagem"
              className={cn(
                "rolagem-fina min-h-11 flex-1 resize-none rounded-lg border px-3 py-2.5 text-[15px] leading-snug placeholder:text-neutral-400 disabled:cursor-not-allowed disabled:opacity-60",
                interna
                  ? "border-warning-500/40 bg-warning-100/40"
                  : "border-neutral-200 bg-neutral-50 focus:bg-white",
              )}
            />
            {podeGravar && !bloqueado ? (
              <Botao
                tamanho="icone"
                variante="secundario"
                className="size-11"
                onClick={() => void gravador.iniciar()}
                aria-label="Gravar áudio"
                title="Gravar áudio"
              >
                <Mic size={19} strokeWidth={1.75} />
              </Botao>
            ) : (
              <Botao
                tamanho="icone"
                onClick={submeter}
                disabled={bloqueado || (!texto.trim() && !anexo)}
                aria-label={interna ? "Salvar nota" : "Enviar"}
                className="size-11"
              >
                {enviarTexto.isPending || enviarAnexo.isPending ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <Send size={18} strokeWidth={1.75} />
                )}
              </Botao>
            )}
          </>
        )}
      </div>
      {termo !== null && !sugestoes.length && (
        <p className="mx-auto max-w-3xl px-4 pb-2 text-xs text-neutral-500">
          Nenhuma resposta com “/{termo}”.{" "}
          <button
            className="font-semibold text-gold-700 hover:underline"
            onClick={() => setGerenciandoRespostas(true)}
          >
            <Settings2 size={12} className="mr-0.5 inline" /> Gerenciar respostas
          </button>
        </p>
      )}

      <PainelModelos
        conversa={conversa}
        aberto={modelosAbertos}
        onFechar={() => setModelosAbertos(false)}
      />
      <PainelLateral
        aberto={gerenciandoRespostas}
        onFechar={() => setGerenciandoRespostas(false)}
        titulo="Respostas rápidas"
      >
        <GerenciarRespostas />
      </PainelLateral>
    </div>
  );
}

function BotaoIcone({
  children,
  titulo,
  onClick,
  ativo,
  tom,
  disabled,
  className,
}: {
  children: React.ReactNode;
  titulo: string;
  onClick: () => void;
  ativo?: boolean;
  tom?: "alerta";
  disabled?: boolean;
  className?: string;
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={ativo}
      title={titulo}
      aria-label={titulo}
      disabled={disabled}
      className={cn(
        "flex size-11 shrink-0 items-center justify-center rounded-md transition-colors disabled:opacity-40",
        ativo && tom === "alerta"
          ? "bg-warning-100 text-warning-500"
          : "text-neutral-500 hover:bg-neutral-100",
        className,
      )}
    >
      {children}
    </button>
  );
}

function PreviaAnexo({ arquivo, onRemover }: { arquivo: File; onRemover: () => void }) {
  const imagem = arquivo.type.startsWith("image/");
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!imagem) return;
    const u = URL.createObjectURL(arquivo);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [arquivo, imagem]);
  return (
    <div className="mx-auto flex max-w-3xl items-center gap-3 px-3 pt-2">
      <div className="flex min-w-0 flex-1 items-center gap-3 rounded-lg border border-neutral-200 bg-neutral-50 p-2">
        {url ? (
          <img src={url} alt="" className="size-14 rounded-md object-cover" />
        ) : (
          <FileText size={28} strokeWidth={1.5} className="text-gold-700" />
        )}
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold text-neutral-900">
            {arquivo.name}
          </span>
          <span className="text-xs text-neutral-500">{formatarTamanho(arquivo.size)}</span>
        </span>
      </div>
      <button
        onClick={onRemover}
        aria-label="Remover anexo"
        className="flex size-9 items-center justify-center rounded-md text-neutral-500 hover:bg-neutral-100"
      >
        <X size={18} strokeWidth={1.75} />
      </button>
    </div>
  );
}
